<template>
    <div
        :class="[
            'grid gap-2',
            $page.url === '/television'
                ? 'grid-cols-1 md:grid-cols-2'
                : 'grid-cols-1',
        ]"
    >
        <div
            v-for="show in shows.slice(0, count)"
            :key="show.tvdbId"
            class="tv-up-card flex flex-col overflow-hidden rounded"
            itemscope
            itemtype="http://schema.org/TVEpisode"
        >
            <!-- Banner with overlays -->
            <div class="relative flex-shrink-0 overflow-hidden">
                <img
                    v-if="show.series.banner && !imageErrors[show?.series_id]"
                    :ref="`img-${show?.series_id}`"
                    :alt="show.series?.title"
                    class="block w-full origin-center object-cover"
                    :src="show.series.banner"
                    @error="imageErrors[show?.series_id] = true"
                />
                <TvPlaceholder
                    v-if="!show.series.banner || imageErrors[show?.series_id]"
                    :title="show.series?.title"
                    :width="400"
                    :height="96"
                />

                <!-- Air date badge -->
                <div
                    class="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-xs font-medium uppercase tracking-wide backdrop-blur-sm"
                    :class="
                        isToday(show.airdate)
                            ? 'text-green-400'
                            : 'text-slate-300'
                    "
                    itemprop="datePublished"
                >
                    {{ formatDate(show.airdate, 'ddd MMM D') }}
                </div>

                <!-- Title overlay -->
                <div
                    class="to-transparent absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black via-black/20 px-3 py-1"
                >
                    <h3
                        class="title-shadow text-base font-bold leading-tight tracking-wide text-white drop-shadow"
                        itemprop="name"
                    >
                        {{ show.series?.title }}
                    </h3>
                    <h4 class="truncate text-xs leading-tight text-slate-300">
                        {{ show.title }}
                    </h4>
                </div>
            </div>

            <!-- Metadata footer -->
            <div
                class="flex items-center gap-3 border-t border-slate-700/40 px-3 py-2 text-xs text-slate-400"
            >
                <span class="shrink-0 font-semibold text-slate-200">
                    S<span
                        itemprop="partOfSeason"
                        itemscope
                        itemtype="http://schema.org/TVSeason"
                        ><span itemprop="seasonNumber">{{
                            show.season
                        }}</span></span
                    >E<span itemprop="episodeNumber">{{
                        show.episode_number
                    }}</span>
                </span>
                <span v-if="show.series?.network" class="truncate">{{
                    show.series.network
                }}</span>
                <span v-if="show.series.runtime" class="shrink-0"
                    >{{ show.series.runtime }} min</span
                >
                <div class="ml-auto flex shrink-0 gap-2">
                    <a
                        v-if="show.series.tvdbId"
                        :href="`https://www.thetvdb.com/search?query=${show.series.tvdbId}`"
                        rel="nofollow"
                        target="_blank"
                        >TVDB</a
                    >
                    <a
                        v-if="show.series.imdbId"
                        :href="`https://www.imdb.com/title/${show.series.imdbId}`"
                        rel="nofollow"
                        target="_blank"
                        >IMDB</a
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import { useGeneralStore } from '@/utils/useGeneralStore.js';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Link } from '@inertiajs/vue3';
import TvPlaceholder from '@/Components/TvPlaceholder.vue';
import { gsap } from 'gsap/all';
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

export default {
    name: 'TelevisionUpcomingShows',
    components: { Link, TvPlaceholder },
    emits: ['complete'],
    props: {
        assetsPath: {
            type: String,
        },
        shows: {
            type: Object,
        },
        count: {
            type: Number,
            default: 4,
        },
    },
    computed: {
        ...mapState(useGeneralStore, [
            'bbSectionHeader',
            'headerLink',
            'headerClass',
            'subheaderClass',
        ]),
    },
    data() {
        return {
            imageErrors: {},
        };
    },
    mounted() {
        const vm = this;
        gsap.fromTo(
            '.tv-up-card',
            { y: 20, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                stagger: 0.025,
                duration: 0.25,
                onComplete: () => {
                    vm.$emit('complete', true);
                },
            },
        );
    },
    methods: {
        formatDate(d, f = 'YYYY-MM-DD') {
            return dayjs(d).format(f);
        },
        isToday(date) {
            return dayjs(date).isSame(dayjs(), 'day');
        },
    },
};
</script>

<style lang="postcss">
.tv-up-card {
    opacity: 0;
}
</style>
